import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  /**
   * True once this browser's user has explicitly picked a theme here (the
   * toggle, or the Settings dropdown). Persisted alongside `theme`.
   *
   * It exists because the server value and the local value can disagree, and
   * only one of them can win. An explicit local choice wins: a device the user
   * has actually told what to do must not be overruled on the next reload by a
   * preference row that may be months old. A device that has never been told
   * anything adopts the account-level choice, which is the whole point of
   * storing it server-side.
   */
  userChosen: boolean;

  // Actions
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  /**
   * Adopt the theme the server has stored for this user (UserPreference.theme),
   * so the choice follows them to a second device.
   *
   * SEEDS THE SESSION, ONCE. The ['preferences'] query this is fed from is a
   * react-query query: it refetches on window focus and is invalidated by
   * useUpdatePreferences. If hydration ran on every one of those, a user who
   * flipped the ThemeToggle mid-session would be silently reset to the server
   * value the moment they tabbed away and back -- and because the toggle writes
   * only to the store, the server value is stale and would keep winning. So the
   * first usable server value wins and every later one is ignored; the live
   * store is authoritative from then until the next page load.
   *
   * The value comes off the wire, so anything that is not one of the three known
   * themes is ignored rather than silently resetting the user to light -- and an
   * unusable value does not burn the one shot, because `undefined` is just what
   * an in-flight query looks like.
   */
  hydrateFromServerPreference: (theme: unknown) => void;
}

const THEMES: Theme[] = ['light', 'dark', 'system'];

const isTheme = (value: unknown): value is Theme =>
  typeof value === 'string' && (THEMES as string[]).includes(value);

/**
 * One-shot latch for hydrateFromServerPreference. Module-level rather than a
 * store field on purpose: it is per page load, it must never be persisted, and
 * keeping it out of the store means the "server already agrees" case performs
 * NO store write at all (which the tests assert by subscribing a spy).
 */
let serverPreferenceHydrated = false;

/** Test-only: a page load is the natural reset, and tests cannot do one. */
export const __resetServerPreferenceHydration = () => {
  serverPreferenceHydrated = false;
};

/**
 * localStorage is not guaranteed to exist, and is not guaranteed not to throw:
 * a private window, blocked site data or a full quota all raise here. This
 * codebase has been burned by assuming the read succeeds, so every access is
 * wrapped and a failure degrades to "no persisted preference" rather than an
 * exception during module import (which would take the whole app down).
 */
const safeStorage: StateStorage = {
  getItem: (name) => {
    try {
      return window.localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      window.localStorage.setItem(name, value);
    } catch {
      /* preference simply will not survive this reload */
    }
  },
  removeItem: (name) => {
    try {
      window.localStorage.removeItem(name);
    } catch {
      /* nothing to do */
    }
  },
};

const getSystemTheme = (): 'light' | 'dark' => {
  try {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
  } catch {
    /* fall through to light */
  }
  return 'light';
};

const resolveTheme = (theme: Theme): 'light' | 'dark' => {
  if (theme === 'system') {
    return getSystemTheme();
  }
  return theme;
};

const applyTheme = (resolvedTheme: 'light' | 'dark') => {
  if (typeof document !== 'undefined') {
    const root = document.documentElement;
    if (resolvedTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'system',
      resolvedTheme: getSystemTheme(),
      userChosen: false,

      setTheme: (theme) => {
        const resolvedTheme = resolveTheme(theme);
        applyTheme(resolvedTheme);
        set({ theme, resolvedTheme, userChosen: true });
      },

      toggleTheme: () => {
        const currentResolved = get().resolvedTheme;
        const newTheme = currentResolved === 'light' ? 'dark' : 'light';
        applyTheme(newTheme);
        set({ theme: newTheme, resolvedTheme: newTheme, userChosen: true });
      },

      hydrateFromServerPreference: (theme) => {
        // Not a usable value (query still in flight, or the server sent
        // something we do not recognise): ignore it and keep the one shot.
        if (!isTheme(theme)) return;
        // Already seeded this session -- a focus refetch must not overrule the
        // choice the user just made in the UI.
        if (serverPreferenceHydrated) return;
        serverPreferenceHydrated = true;
        // This browser has an explicit choice of its own. It wins, and it keeps
        // winning across reloads. Without this, wiring the server -> client half
        // while the client -> server half is missing makes a toggle click stop
        // surviving a reload, which is a worse defect than the one being fixed.
        if (get().userChosen) return;
        // Server agrees with the local choice: nothing to write.
        if (get().theme === theme) return;
        const resolvedTheme = resolveTheme(theme);
        applyTheme(resolvedTheme);
        set({ theme, resolvedTheme });
      },
    }),
    {
      name: 'policy-theme-storage',
      storage: createJSONStorage(() => safeStorage),
      partialize: (state) => ({
        theme: state.theme,
        userChosen: state.userChosen,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          const theme = isTheme(state.theme) ? state.theme : 'system';
          state.theme = theme;
          state.userChosen = state.userChosen === true;
          const resolvedTheme = resolveTheme(theme);
          state.resolvedTheme = resolvedTheme;
          applyTheme(resolvedTheme);
        }
      },
    }
  )
);

/**
 * Apply the theme class at the ROOT of the app and keep it there.
 *
 * To be accurate about what this did and did not fix: login and public pages
 * were NOT rendering without the class before. App.tsx statically imports
 * DashboardLayout, whose barrel pulls in Sidebar, which imports '@/store',
 * which evaluates this module -- so persist's onRehydrateStorage called
 * applyTheme() during module evaluation on every route. The old useEffect in
 * DashboardLayout was redundancy, not the only coverage, and moving it here
 * does not change WHICH routes get the class.
 *
 * What it does buy is a single owner for the class plus a subscription, so any
 * future path that mutates the store without going through setTheme (a devtools
 * poke, a rehydrate, another tab) still ends up on screen. The white flash on
 * hard load is fixed by the pre-paint inline script in index.html, not by this.
 *
 * Returns an unsubscribe so a test (or a future host) can tear it down.
 */
export const initTheme = (): (() => void) => {
  applyTheme(useThemeStore.getState().resolvedTheme);
  return useThemeStore.subscribe((state) => applyTheme(state.resolvedTheme));
};

// Listen for system theme changes
if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  try {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      const state = useThemeStore.getState();
      if (state.theme === 'system') {
        const resolvedTheme = e.matches ? 'dark' : 'light';
        applyTheme(resolvedTheme);
        useThemeStore.setState({ resolvedTheme });
      }
    });
  } catch {
    /* older engines without addEventListener on MediaQueryList */
  }
}
